import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const EMPTY = { name: "", days: "" };

export default function PaymentTermsPage() {
  const { isAdmin } = useAuth();
  const [terms, setTerms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const res = await api.get("/payment-terms");
    setTerms(res.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, days: String(t.days) });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = { name: form.name, days: Number(form.days) };
    try {
      if (editing) {
        await api.patch(`/payment-terms/${editing.id}`, payload);
        toast.success("Payment term updated");
      } else {
        await api.post("/payment-terms", payload);
        toast.success("Payment term created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/payment-terms/${t.id}`, { active: !t.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="payment-terms-page">
      <PageHeader
        title="Payment Terms"
        subtitle="Centrally administered — expected payment date = invoice date + days"
        actions={
          isAdmin && (
            <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-payment-term-button">
              <Plus className="h-4 w-4 mr-1.5" /> New payment term
            </Button>
          )
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden max-w-3xl" data-testid="payment-terms-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Name</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Days</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {terms.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`payment-term-row-${t.days}`}>
                <td className="py-3 px-4 text-sm font-semibold text-slate-800">{t.name}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{t.days}</td>
                <td className="py-3 px-4"><ActiveBadge active={t.active} testId={`payment-term-status-${t.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-payment-term-${t.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(t)} data-testid={`toggle-payment-term-${t.id}`}>
                        {t.active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {terms.length === 0 && <EmptyState title="No payment terms yet" testId="payment-terms-empty" />}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="payment-term-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit payment term" : "New payment term"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="45 days" data-testid="payment-term-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Days</Label>
              <Input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="payment-term-days-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="payment-term-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="payment-term-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
